import React, { useState, useEffect, useRef } from 'react';
import { X, ExternalLink, ChevronDown, ChevronUp, Sparkles, Copy, Check, Mail } from 'lucide-react';
import { normalizeSourceName, parseSourceUrl, getFaviconUrl } from '../../utils/leadPanelUtils';
import { isEmail } from '../../utils/validation';

interface EnrichmentDetailsPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRect: DOMRect | null;
  columnName: string;
  rowName: string;
  cellValue: string;
  enrichmentSummary: string;
  dataSources: string[];
  isLoading?: boolean;
  onStartCampaign?: () => void; // Trigger email campaign flow
}

export const EnrichmentDetailsPopover: React.FC<EnrichmentDetailsPopoverProps> = ({
  isOpen,
  onClose,
  anchorRect,
  columnName,
  rowName,
  cellValue,
  enrichmentSummary,
  dataSources,
  isLoading = false,
  onStartCampaign,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0, placement: 'bottom' as 'bottom' | 'top', arrowLeft: 170 });
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  // Parse sources
  const sources = dataSources.map(parseSourceUrl).filter((s) => s !== null);
  const hasMoreText = enrichmentSummary && enrichmentSummary.length > 200;

  // Estimate height for positioning
  const estimatedHeight = isExpanded ? 480 : 360;

  useEffect(() => {
    if (!isOpen) {
      setIsVisible(false);
      setIsExpanded(false);
      return;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !anchorRect) return;

    const w = 360;
    const pad = 16;
    const off = 8;

    let left = anchorRect.left + anchorRect.width / 2 - w / 2;
    let arrowLeft = w / 2;

    if (left < pad) {
      arrowLeft -= (pad - left);
      left = pad;
    } else if (left + w > window.innerWidth - pad) {
      const ov = left + w - (window.innerWidth - pad);
      arrowLeft += ov;
      left = window.innerWidth - w - pad;
    }
    arrowLeft = Math.max(24, Math.min(w - 24, arrowLeft));

    let top = anchorRect.bottom + off + 6;
    let placement: 'bottom' | 'top' = 'bottom';

    if (top + estimatedHeight > window.innerHeight - pad) {
      top = anchorRect.top - off - estimatedHeight - 6;
      placement = 'top';
      if (top < pad) {
        top = anchorRect.bottom + off + 6;
        placement = 'bottom';
      }
    }

    setPosition({ top, left, placement, arrowLeft });
    requestAnimationFrame(() => setIsVisible(true));
  }, [isOpen, anchorRect, estimatedHeight]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        handleClose();
      }
    };
    const timer = setTimeout(() => document.addEventListener('mousedown', handleClickOutside), 50);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 150);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(cellValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (!isOpen || !anchorRect) return null;

  return (
    <>
      <style>{`
        @keyframes popIn { from { opacity: 0; transform: scale(0.96) translateY(4px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        @keyframes popOut { from { opacity: 1; } to { opacity: 0; transform: scale(0.98); } }
        .enrichment-scroll::-webkit-scrollbar { width: 4px; }
        .enrichment-scroll::-webkit-scrollbar-track { background: transparent; }
        .enrichment-scroll::-webkit-scrollbar-thumb { background: #E5E7EB; border-radius: 2px; }
        .enrichment-scroll::-webkit-scrollbar-thumb:hover { background: #D1D5DB; }
      `}</style>

      <div className="fixed inset-0 z-[299]" onMouseDown={(e) => { e.stopPropagation(); handleClose(); }} />

      {/* Arrow */}
      <div
        className="fixed z-[301] transition-opacity duration-150"
        style={{
          left: position.left + position.arrowLeft - 6,
          top: position.placement === 'bottom' ? position.top - 6 : position.top + estimatedHeight,
          opacity: isVisible ? 1 : 0,
        }}
      >
        <svg width="12" height="6" viewBox="0 0 12 6" fill="none">
          <path d={position.placement === 'bottom' ? "M6 0L12 6H0L6 0Z" : "M6 6L0 0H12L6 6Z"} fill="white" />
        </svg>
      </div>

      <div
        ref={popoverRef}
        className="fixed z-[300] bg-white rounded-2xl flex flex-col"
        style={{
          top: position.top,
          left: position.left,
          width: 360,
          maxHeight: isExpanded ? 520 : 400,
          animation: isVisible ? 'popIn 180ms cubic-bezier(0.16, 1, 0.3, 1) forwards' : 'popOut 120ms ease-out forwards',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 10px 15px -3px rgba(0,0,0,0.08), 0 20px 25px -5px rgba(0,0,0,0.08)',
          transition: 'max-height 200ms ease',
        }}
      >
        {/* Header */}
        <div className="px-3 py-2.5 flex items-center justify-between border-b border-gray-50 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-md bg-gray-900 flex items-center justify-center">
              <Sparkles size={11} className="text-white" />
            </div>
            <div className="min-w-0">
              <span className="text-[11px] font-semibold text-gray-900 block truncate max-w-[180px]">
                {columnName}
              </span>
              <span className="text-[10px] text-gray-400 block truncate max-w-[180px]">
                {rowName}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-0.5">
            <button
              onClick={handleCopy}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
              title="Copy value"
            >
              {copied ? <Check size={13} className="text-green-500" /> : <Copy size={13} />}
            </button>
            {onStartCampaign && isEmail(cellValue) && (
              <button
                onClick={() => {
                  handleClose();
                  onStartCampaign();
                }}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
                title="Start outbound campaign"
              >
                <Mail size={13} />
              </button>
            )}
            <button
              onClick={handleClose}
              className="p-1.5 rounded-lg text-gray-300 hover:text-gray-500 hover:bg-gray-100 transition-all"
            >
              <X size={13} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto enrichment-scroll">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <div className="w-8 h-8 rounded-full border-2 border-gray-200 border-t-gray-900 animate-spin mb-3" />
              <p className="text-[12px] text-gray-500">Loading...</p>
            </div>
          ) : (
            <div className="p-3 space-y-3">
              {/* Value Section */}
              <div className="rounded-xl bg-gray-50 p-3">
                <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-1.5">
                  Value
                </p>
                <p className="text-[13px] font-medium text-gray-900 leading-relaxed break-words">
                  {cellValue || <span className="text-gray-400 italic">No value</span>}
                </p>
              </div>

              {/* Reasoning Section */}
              {enrichmentSummary && (
                <div>
                  <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-1.5 px-1">
                    Reasoning
                  </p>
                  <p className="text-[12px] text-gray-600 leading-[1.7] px-1">
                    {isExpanded ? enrichmentSummary : (
                      enrichmentSummary.length > 200
                        ? enrichmentSummary.slice(0, 200) + '...'
                        : enrichmentSummary
                    )}
                  </p>
                  {hasMoreText && (
                    <button
                      onClick={() => setIsExpanded(!isExpanded)}
                      className="mt-2 px-1 text-[11px] font-medium text-blue-600 hover:text-blue-700 flex items-center gap-0.5 transition-colors"
                    >
                      {isExpanded ? <>Show less <ChevronUp size={12} /></> : <>Show more <ChevronDown size={12} /></>}
                    </button>
                  )}
                </div>
              )}

              {/* Sources Section */}
              {sources.length > 0 && (
                <div className="pt-3 border-t border-gray-100">
                  <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-2 px-1">
                    References ({sources.length})
                  </p>
                  <div className="space-y-1.5">
                    {sources.slice(0, 5).map((source, idx) => {
                      if (!source) return null;
                      const displayName = source.title || normalizeSourceName(source.hostname);
                      const favicon = getFaviconUrl(source.hostname, 32);

                      return (
                        <a
                          key={idx}
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 transition-colors group"
                        >
                          <div className="w-6 h-6 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <img
                              src={favicon}
                              alt=""
                              className="w-3.5 h-3.5"
                              onError={(e: any) => { e.target.style.opacity = '0.3'; }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1">
                              <span className="text-[12px] font-medium text-gray-800 truncate group-hover:text-blue-600 transition-colors">
                                {displayName}
                              </span>
                              <ExternalLink size={10} className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                            </div>
                            <p className="text-[10px] text-gray-400 truncate">
                              {source.hostname}
                            </p>
                          </div>
                        </a>
                      );
                    })}
                    {sources.length > 5 && (
                      <p className="text-[10px] text-gray-400 px-2 py-1">
                        +{sources.length - 5} more references
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Empty state */}
              {!enrichmentSummary && sources.length === 0 && cellValue && (
                <div className="text-center py-4">
                  <p className="text-[11px] text-gray-400">No additional details available</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
